"use client";

import { Language } from '@/lib/i18n';
import { getLanguageFromPath } from '@/lib/routes';
import { usePathname } from 'next/navigation';
import { ReactNode, createContext, useContext, useMemo, useState } from 'react';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const pathname = usePathname();

  // Detect language from pathname immediately (not in useEffect) to avoid hydration mismatch
  const detectedLanguage = useMemo(() => {
    return getLanguageFromPath(pathname || '/');
  }, [pathname]);

  const [manualLanguage, setManualLanguage] = useState<Language | null>(null);

  // Use manual language if set, otherwise use detected language from URL
  const language = manualLanguage ?? detectedLanguage;

  const setLanguage = (lang: Language) => {
    setManualLanguage(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}